import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, UserCircle2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/events", label: "Events" },
  { to: "/conferences", label: "Conferences" },
  { to: "/membership", label: "Membership" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    nav("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-white" data-testid="main-navbar">
      {/* Top utility toolbar — mirrors ser.org */}
      <div className="ser-toolbar hidden md:block">
        <div className="ser-container px-6 md:px-12 py-1.5 flex justify-end gap-4 text-[0.75rem]">
          <a href="https://www.ser.org" target="_blank" rel="noopener noreferrer">SER Global</a>
          <span className="opacity-50">|</span>
          <Link to="/membership">Join</Link>
          <span className="opacity-50">|</span>
          <Link to="/contact">Contact</Link>
          {!isAuthenticated && (
            <>
              <span className="opacity-50">|</span>
              <Link to="/login" data-testid="toolbar-signin">Sign In</Link>
            </>
          )}
        </div>
      </div>

      {/* Main bar */}
      <nav
        className="ser-container flex items-center justify-between h-20 px-6 md:px-12 border-b"
        style={{ borderColor: "var(--ser-border)" }}
      >
        <BrandLogo height={88} linkTo="/" transparent darkenGrey />

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `text-[0.78rem] font-bold uppercase tracking-[0.12em] transition-colors py-2 border-b-2 ${
                  isActive
                    ? "text-[var(--ser-primary)] border-[var(--ser-primary)]"
                    : "text-[var(--ser-teal)] border-transparent hover:text-[var(--ser-primary)]"
                }`
              }
              style={{ fontFamily: "Lato, sans-serif" }}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          {isAuthenticated ? (
            <>
              {isAdmin && (
                <Link to="/admin" className="btn-ser-ghost" data-testid="nav-admin-link">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to="/dashboard" className="btn-ser-ghost" data-testid="nav-dashboard-link">
                <UserCircle2 className="w-4 h-4" />
                {user?.name?.split(" ")[0] || "My Account"}
              </Link>
              <button className="btn-ser-outline" onClick={handleLogout} data-testid="nav-logout-btn">
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ser-ghost" data-testid="nav-login-link">
                Login
              </Link>
              <Link to="/register" className="btn-ser-primary" data-testid="nav-register-link">
                Join Us
              </Link>
            </>
          )}
        </div>

        {/* Mobile trigger */}
        <button
          className="lg:hidden p-2 rounded-md"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? (
            <X className="w-6 h-6" style={{ color: "var(--ser-primary)" }} />
          ) : (
            <Menu className="w-6 h-6" style={{ color: "var(--ser-primary)" }} />
          )}
        </button>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden border-t px-6 py-6 flex flex-col gap-3"
          style={{ backgroundColor: "#fff", borderColor: "var(--ser-border)" }}
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-3 py-2 rounded-sm text-sm font-bold uppercase tracking-[0.1em] ${
                  isActive
                    ? "bg-[var(--ser-section-alt)] text-[var(--ser-primary)]"
                    : "text-[var(--ser-teal)]"
                }`
              }
              style={{ fontFamily: "Lato, sans-serif" }}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-3 border-t flex flex-col gap-3" style={{ borderColor: "var(--ser-border)" }}>
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="btn-ser-ghost justify-start" data-testid="mobile-admin-link">
                    <ShieldCheck className="w-4 h-4" /> Admin
                  </Link>
                )}
                <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-ser-ghost justify-start" data-testid="mobile-dashboard-link">
                  <UserCircle2 className="w-4 h-4" /> My Account
                </Link>
                <button className="btn-ser-outline" onClick={handleLogout} data-testid="mobile-logout-btn">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ser-outline" data-testid="mobile-login-link">
                  Login
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-ser-primary" data-testid="mobile-register-link">
                  Join Us
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
